import React, { useState } from "react";
import { useDispatch } from "react-redux";
import {
  updateContacts,
  updateImportantInfo,
} from "../../../redux/slices/careplanSlice";
import styled from "styled-components";
import { DeleteIcon, PlusIcon } from "../../common/Icons";
import TextField from "../../common/TextField";
import IconButton from "../../common/Button";

const UrgentCare = ({
  retrievedContacts,
  initialImportantInfo,
  mode = "edit",
}) => {
  const dispatch = useDispatch();
  const [contacts, setContacts] = useState(retrievedContacts || []);
  const [newContact, setNewContact] = useState({
    name: "",
    number: "",
  });
  const [info, setInfo] = useState(initialImportantInfo || "");
  const [errors, setErrors] = useState({});

  // Handle input changes for new contact
  const handleChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;

    if (name === "number") {
      formattedValue = formatUKPhoneNumber(value);
    }

    setNewContact((prev) => ({
      ...prev,
      [name]: formattedValue,
    }));
  };

  // Handle input changes for new contact
  const handleInfoChange = (e) => {
    const updatedInfo = e.target.value;
    setInfo(updatedInfo);
    // Dispatch the action to update the important_info field in Redux
    dispatch(updateImportantInfo(updatedInfo));
  };

  // UK Phone Number format validation (01274 497121 / 020 79460000 / 0161 496 0212 / 0131 496 0212)
  const isValidUKPhoneNumber = (number) => {
    const ukPhoneNumberRegex = /^0\d{3,4}\s?\d{5,7}$/;
    return ukPhoneNumberRegex.test(number);
  };

  // Validate and Add new contact to the list
  const addContact = (e) => {
    e.preventDefault(); // Prevent page refresh

    let errorMessages = {};

    if (!newContact.name) {
      errorMessages.name = "Name is required.";
    }

    if (!isValidUKPhoneNumber(newContact.number)) {
      errorMessages.number = "Please enter a valid UK phone number.";
    }

    if (Object.keys(errorMessages).length > 0) {
      setErrors(errorMessages);
    } else {
      const updatedContacts = [...contacts, newContact];
      setContacts(updatedContacts);
      setNewContact({ name: "", number: "" });
      setErrors({});

      // Dispatch updated contacts to Redux
      dispatch(updateContacts(updatedContacts));
    }
  };

  // Remove contact from the list
  const deleteContact = (index) => {
    const updatedContacts = contacts.filter((_, i) => i !== index);
    setContacts(updatedContacts);

    // Dispatch updated contacts to Redux
    dispatch(updateContacts(updatedContacts));
  };

  const formatUKPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, "");
    let formattedNumber = "";

    if (cleaned.length <= 5) {
      // If the input is up to 5 digits, treat it as part of the area code
      formattedNumber = cleaned;
    } else if (cleaned.length <= 11) {
      // Format as '01274 497121' when the total number is between 6 and 11 digits
      formattedNumber = `${cleaned.slice(0, 5)} ${cleaned.slice(5)}`;
    } else {
      // For longer inputs (though in the UK format, 11 digits are typical), break it into parts
      formattedNumber = `${cleaned.slice(0, 5)} ${cleaned.slice(
        5,
        9
      )} ${cleaned.slice(9, 13)}`;
    }

    return formattedNumber;
  };

  return (
    <Container>
      <EmergencyHeader>
        If my chest health gets worse, I am showing signs of sepsis or you are
        very worried about me, You should seek urgent medical attention
      </EmergencyHeader>

      <InputSection>
        <SectionTitle>
          Important information if I need to go into hospital
        </SectionTitle>
        {mode !== "view" ? (
          <>
            <p className="body-text-regular regular neutral-700">
              Enter the important information if the service user needs urgent
              medical attention.
            </p>
            <TextAreaWrapper>
              <TextField
                type="text"
                name="info"
                placeholder="Enter the information"
                maxLength={1000}
                showCharacterLimit={true}
                isTextArea={true}
                height="275px"
                value={info}
                style={{ width: "100%" }}
                onChange={handleInfoChange}
              />
            </TextAreaWrapper>
          </>
        ) : (
          <InfoText>
            {initialImportantInfo || "No information provided"}
          </InfoText>
        )}

        {/* <TextArea placeholder="Enter the important information..."></TextArea> */}
      </InputSection>

      <ContactSection>
        <SectionTitle>Useful contacts</SectionTitle>

        <ContactList>
          {contacts.map((contact, index) => (
            <ContactItem key={index} mode={mode}>
              <span>
                {contact.name}: {contact.number}
              </span>
              {mode !== "view" && (
                <DeleteButton
                  onClick={(e) => {
                    e.preventDefault();
                    deleteContact(index);
                  }}
                >
                  <DeleteIcon color="#EC2D30" />
                </DeleteButton>
              )}
            </ContactItem>
          ))}
        </ContactList>

        {mode !== "view" && (
          <>
            <InputContainerWrapper>
              <InputContainer>
                <label
                  htmlFor="contact-name"
                  className="body-text-regular medium neutral-700"
                >
                  Person’s name
                </label>
                <TextField
                  type="text"
                  name="name"
                  placeholder="Enter name"
                  value={newContact.name}
                  onChange={handleChange}
                  style={{ width: "100%" }}
                  error={!!errors.name}
                  errorMessage={errors.name}
                />
                {/* {errors.name && <ErrorText>{errors.name}</ErrorText>} */}
              </InputContainer>

              <InputContainer>
                <label
                  htmlFor="contact-number"
                  className="body-text-regular medium neutral-700"
                >
                  Contact number
                </label>
                <TextField
                  type="text"
                  name="number"
                  placeholder="Enter contact number"
                  value={newContact.number}
                  style={{ width: "100%" }}
                  onChange={handleChange}
                  error={!!errors.number}
                  errorMessage={errors.number}
                />
              </InputContainer>
              {/* {errors.number && <ErrorText>{errors.number}</ErrorText>} */}
            </InputContainerWrapper>

            <IconButton
              variant="ghost"
              state="idle"
              icon={<PlusIcon color="#004F96" />}
              iconPosition="left"
              type="button" // Type is "button" because it is not related to form submission
              onClick={addContact}
            >
              Add contact
            </IconButton>
          </>
        )}
      </ContactSection>
    </Container>
  );
};

// Styled components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 32px;
  align-self: stretch;
`;

const EmergencyHeader = styled.h6`
  color: #ec2d30;
  font-weight: 600;

  @media print {
    background: rgba(236, 45, 48, 0.2);
    border-radius: 12px;
    padding: 16px;
    margin: 0;
  }
`;

const InputSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
`;

const TextAreaWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const ContactSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  align-self: stretch;
`;

const SectionTitle = styled.h6`
  color: #161718;
  font-weight: 600;
`;

const ContactList = styled.div`
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 16px;

  @media print {
    gap: 0px !important;
  }
`;

const ContactItem = styled.div`
  display: flex;
  padding: 12px 24px;
  align-items: center;
  gap: 16px;
  border-radius: 100px;
  border: 1px solid
    ${(props) => (props.mode === "view" ? "#89dee4" : "#ced4da")};
  background: ${(props) => (props.mode === "view" ? "#e6f8f9" : "#f8f9fa")};

  @media print {
    background: none !important;
    border: none !important;
`;

const DeleteButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  height: 20px;
  width: 20px;
`;

const InputContainerWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  width: 100%;
  gap: 24px;
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  flex: 1;
`;

const InfoText = styled.div``;

export default UrgentCare;
